import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { groupWorkoutsByDate } from '../utils/csvParser';
import { DateOnly } from '../utils/DateOnly';
import { isFrielWorkout } from '../utils/workoutTypes';
import WorkoutFormModal from './WorkoutFormModal';
import ImportWorkoutModal from './ImportWorkoutModal';
import WeeklySummary from './WeeklySummary';
import CalendarHeader from './CalendarHeader';
import CalendarGrid from './CalendarGrid';
import ExportModal from './ExportModal';
import TravelPlansModal from './TravelPlansModal';
import { useCalendarNavigation } from '../hooks/useCalendarNavigation';
import { useCalendarDragDrop } from '../hooks/useCalendarDragDrop';
import { API_ENDPOINTS, apiCall } from '../config/api';
import '../styles/Calendar.css';

/**
 * Calendar component displays workouts in a weekly or monthly calendar view
 * @param {Array} workouts - Array of workout objects
 * @param {Object} triClubSchedule - Tri club schedule with weekly events
 * @param {DateOnly} initialDate - Starting date (defaults to today)
 * @param {Function} onWorkoutSelectionToggle - Callback for when user toggles workout selection
 * @param {Function} onWorkoutDateChange - Callback for when user drags workout to a new date
 * @param {Function} onWorkoutTimeOfDayChange - Callback for when user drags workout to a time of day
 * @param {Function} onWorkoutLocationChange - Callback for when user changes workout location
 * @param {Function} onExportToCalendar - Callback for exporting workouts to calendar
 * @param {Function} onAddCustomWorkout - Callback for adding a custom workout
 * @param {Function} onImportWorkouts - Callback for importing workouts from CSV file
 * @param {Function} onEditWorkout - Callback for after editing a workout (to reload workouts)
 */
function Calendar({ workouts = [], triClubSchedule = null, initialDate = (() => {
  const today = new Date();
  return new DateOnly(today.getFullYear(), today.getMonth() + 1, today.getDate());
})(), onWorkoutSelectionToggle, onWorkoutDateChange, onWorkoutTimeOfDayChange, onWorkoutSelectionUpdate, onWorkoutLocationChange, onWorkoutHeatTrainingChange, onExportToCalendar, onAddCustomWorkout, onImportWorkouts, onEditWorkout, onWeekChange, locations = [], onLocationAdded, initialFilters = null, onFiltersChange = null }) {
  // Use custom hooks for navigation and drag-drop
  const navigation = useCalendarNavigation(initialDate);
  const dragDrop = useCalendarDragDrop();
  
  // Local component state
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isImportWorkoutModalOpen, setIsImportWorkoutModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [formModalInitialDate, setFormModalInitialDate] = useState(null);
  const [formModalInitialTimeOfDay, setFormModalInitialTimeOfDay] = useState(null);
  const [isTravelPlansModalOpen, setIsTravelPlansModalOpen] = useState(false);

  // Source filters state — initialised from prop so URL can seed the value
  const [filters, setFilters] = useState(
    initialFilters || { showFriel: true, showTriClub: true, showOther: true }
  );

  // Notify parent when the displayed week changes
  const { currentDate } = navigation;
  const currentDateRef = React.useRef(currentDate);
  useEffect(() => {
    // Skip the initial render — only fire on navigation
    if (currentDateRef.current === currentDate) return;
    currentDateRef.current = currentDate;
    if (onWeekChange) {
      onWeekChange(currentDate);
    }
  }, [currentDate, onWeekChange]);

  // Derive the up-to-date workout object from the workouts array at render time,
  // so we don't need an effect to sync selectedWorkout with the latest data.
  const displayedWorkout = selectedWorkout
    ? (workouts.find(w => w.id === selectedWorkout.id) ?? selectedWorkout)
    : null;

  // Apply source filters to workouts
  const filteredWorkouts = workouts.filter(w => {
    // Custom workouts are "other"
    if (w.isCustom) {
      return filters.showOther;
    }

    // Friel workouts use a title pattern
    if (isFrielWorkout(w)) {
      return filters.showFriel;
    }

    // Otherwise, consider it tri club
    return filters.showTriClub;
  });

  // Group workouts by date
  const workoutsByDate = groupWorkoutsByDate(filteredWorkouts);

  // Wrapper for handleDrop to pass callbacks (supports combined selection updates)
  const handleDrop = (e, dayObj, timeSlot = null) => {
    dragDrop.handleDrop(e, dayObj, timeSlot, onWorkoutDateChange, onWorkoutTimeOfDayChange, onWorkoutSelectionUpdate);
  };

  // Handler for clicking "Add Custom Workout" button
  const handleAddWorkoutClick = (date = null, timeOfDay = null) => {
    setSelectedWorkout(null);
    setIsEditMode(false);
    setFormModalInitialDate(date);
    setFormModalInitialTimeOfDay(timeOfDay);
    setIsFormModalOpen(true);
  };

  // Handler for clicking on a workout badge to edit
  const handleWorkoutCardClick = (workout) => {
    setSelectedWorkout(workout);
    setIsEditMode(true);
    setIsFormModalOpen(true);
  };

  // Handler for saving workout (both create and edit)
  const handleWorkoutSave = async (workoutData, workoutId = null) => {
    const isEdit = workoutId !== null;

    try {
      if (isEdit) {
        // For edit, Calendar makes the API call directly
        const response = await apiCall(API_ENDPOINTS.WORKOUT_BY_ID(workoutId), {
          method: 'PUT',
          body: JSON.stringify(workoutData)
        });

        if (!response.ok) {
          throw new Error('Failed to update workout');
        }

        if (onEditWorkout) {
          await onEditWorkout();
        }
      } else if (onAddCustomWorkout) {
        // For create, delegate entirely to parent — it owns the API call and state update
        await onAddCustomWorkout(workoutData);
      }

      setIsFormModalOpen(false);
      setSelectedWorkout(null);
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} workout:`, error);
      throw error;
    }
  };

  // Build calendar grid — week view: Mon through Sun of current week
  const days = [];
  const dayOfWeek = (currentDate.getDay() + 6) % 7; // 0 = Monday
  const weekStart = new Date(currentDate);
  weekStart.setDate(weekStart.getDate() - dayOfWeek);

  for (let i = 0; i < 7; i++) {
    const dayDate = new Date(weekStart);
    dayDate.setDate(dayDate.getDate() + i);
    days.push({
      day: dayDate.getDate(),
      year: dayDate.getFullYear(),
      month: dayDate.getMonth(),
      date: new Date(dayDate),
      isCurrentMonth: true
    });
  }

  // Month and year display
  const monthYear = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  }).format(currentDate);

  // Get all workouts for the current week (for weekly summary)
  const getWorkoutsForCurrentWeek = () => {
    const weekWorkouts = [];
    const dayOfWeek = (currentDate.getDay() + 6) % 7; // 0 = Monday
    const weekStart = new Date(currentDate);
    weekStart.setDate(weekStart.getDate() - dayOfWeek);
    
    // Get workouts for all 7 days of the week
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(weekStart);
      dayDate.setDate(dayDate.getDate() + i);
      const dateStr = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
      const dayWorkouts = workoutsByDate[dateStr] || [];
      weekWorkouts.push(...dayWorkouts);
    }
    
    return weekWorkouts;
  };

  // Get the start and end dates of the current week
  // Memoize to prevent unnecessary re-creation of DateOnly objects
  const weekDateRange = useMemo(() => {
    const dayOfWeek = (currentDate.getDay() + 6) % 7; // 0 = Monday
    const weekStart = new Date(currentDate);
    weekStart.setDate(weekStart.getDate() - dayOfWeek);
    
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    
    return {
      start: new DateOnly(weekStart.getFullYear(), weekStart.getMonth() + 1, weekStart.getDate()),
      end: new DateOnly(weekEnd.getFullYear(), weekEnd.getMonth() + 1, weekEnd.getDate())
    };
  }, [currentDate]);

  return (
    <div className="calendar-wrapper">
      <div className="calendar">
        <CalendarHeader
          monthYear={monthYear}
          onNavPrevious={navigation.goToPreviousWeek}
          onNavNext={navigation.goToNextWeek}
          onGoToToday={navigation.goToToday}
          onOpenImport={() => setIsImportWorkoutModalOpen(true)}
          onOpenExport={() => setIsExportModalOpen(true)}
          onOpenAddWorkout={() => handleAddWorkoutClick()}
          onOpenTravelPlans={() => setIsTravelPlansModalOpen(true)}
          filters={filters}
          onFilterChange={(newFilters) => {
            setFilters(newFilters);
            if (onFiltersChange) onFiltersChange(newFilters);
          }}
        />

        <CalendarGrid
          days={days}
          workoutsByDate={workoutsByDate}
          triClubSchedule={triClubSchedule}
          showTimeSlots={dragDrop.showTimeSlots}
          dragState={{
            draggedWorkout: dragDrop.draggedWorkout,
            dragOverDate: dragDrop.dragOverDate,
            dragOverTimeSlot: dragDrop.dragOverTimeSlot
          }}
          onDragOver={dragDrop.handleDragOver}
          onDragLeave={dragDrop.handleDragLeave}
          onDrop={handleDrop}
          onWorkoutClick={handleWorkoutCardClick}
          onWorkoutSelectionToggle={onWorkoutSelectionToggle}
          onWorkoutHeatTrainingChange={onWorkoutHeatTrainingChange}
          onWorkoutDragStart={dragDrop.handleDragStart}
          onWorkoutDragEnd={dragDrop.handleDragEnd}
          onAddWorkout={handleAddWorkoutClick}
          locations={locations}
        />

      <WorkoutFormModal
        key={isEditMode ? `edit-${displayedWorkout?.id}` : `create-${formModalInitialDate}-${formModalInitialTimeOfDay}`}
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedWorkout(null);
        }}
        onSave={handleWorkoutSave}
        workout={displayedWorkout}
        isEditMode={isEditMode}
        initialDate={formModalInitialDate}
        initialTimeOfDay={formModalInitialTimeOfDay}
      />
      
      <ImportWorkoutModal
        isOpen={isImportWorkoutModalOpen}
        onClose={() => setIsImportWorkoutModalOpen(false)}
        onImport={onImportWorkouts}
      />

      <ExportModal
        key={`${weekDateRange.start}-${weekDateRange.end}`}
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={async (dateRange) => {
          if (onExportToCalendar) {
            await onExportToCalendar(dateRange);
          }
        }}
        defaultStartDate={weekDateRange.start}
        defaultEndDate={weekDateRange.end}
      />

      <TravelPlansModal
        isOpen={isTravelPlansModalOpen}
        onClose={() => setIsTravelPlansModalOpen(false)}
        onLocationAdded={(newLocation) => {
          if (onLocationAdded) {
            onLocationAdded(newLocation);
          }
        }}
        locations={locations}
      />
    </div>

    <WeeklySummary
      workouts={getWorkoutsForCurrentWeek()}
      weekStartDate={weekDateRange.start}
      weekEndDate={weekDateRange.end}
      onExportToCalendar={onExportToCalendar}
    />
  </div>
  );
}

Calendar.propTypes = {
  workouts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string.isRequired,
      workoutType: PropTypes.string.isRequired,
      workoutDate: PropTypes.instanceOf(DateOnly).isRequired,
      originallyPlannedDay: PropTypes.string.isRequired,
      workoutDescription: PropTypes.string,
      plannedDuration: PropTypes.number,
      plannedDistanceInMeters: PropTypes.number,
      coachComments: PropTypes.string,
      isSelected: PropTypes.bool,
      timeOfDay: PropTypes.string,
      workoutLocation: PropTypes.string,
    })
  ),
  triClubSchedule: PropTypes.shape({
    effective_date: PropTypes.string,
    schedule: PropTypes.objectOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          time: PropTypes.string.isRequired,
          activity: PropTypes.string.isRequired,
        })
      )
    ),
  }),
  initialDate: PropTypes.instanceOf(DateOnly),
  onWorkoutSelectionToggle: PropTypes.func,
  onWorkoutDateChange: PropTypes.func,
  onWorkoutTimeOfDayChange: PropTypes.func,
  onWorkoutSelectionUpdate: PropTypes.func.isRequired,
  onWorkoutLocationChange: PropTypes.func,
  onWorkoutHeatTrainingChange: PropTypes.func,
  onExportToCalendar: PropTypes.func,
  onAddCustomWorkout: PropTypes.func,
  onImportWorkouts: PropTypes.func,
  onWeekChange: PropTypes.func,
  initialFilters: PropTypes.shape({
    showFriel: PropTypes.bool,
    showTriClub: PropTypes.bool,
    showOther: PropTypes.bool,
  }),
  onFiltersChange: PropTypes.func,
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    cityName: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  })),
  onLocationAdded: PropTypes.func,
};

Calendar.defaultProps = {
  onWorkoutSelectionToggle: null,
  onWorkoutHeatTrainingChange: null,
  onWorkoutDateChange: null,
  onWorkoutTimeOfDayChange: null,
  onWorkoutLocationChange: null,
  onExportToCalendar: null,
  onAddCustomWorkout: null,
  onImportWorkouts: null,
  onWeekChange: null,
  locations: [],
  onLocationAdded: null,
};

Calendar.defaultProps = {
  workouts: [],
  triClubSchedule: null,
  initialDate: (() => {
    const today = new Date();
    return new DateOnly(today.getFullYear(), today.getMonth() + 1, today.getDate());
  })(),
};

export default Calendar;
